import React, { useEffect, useState } from "react";

import { getAllSideCropRecommendation, updateSideCropRecommendation } from "services";

import { saveFile, useCSVReader, useCancelAsyncOnUnmount, usePapaParse } from "utils";

import { AllSideCropRecommendationResponse, CSV, GetRootProps, SideCropRecommendationErrorItem, SideCropRecommendationRequest } from "models";

import { AdminHead, AdminTable, Button, Col, Collapse, Divider, Icon, Row, Tooltip, Message as message } from "components";

const CollapsibleErrors = ({ errors }: { errors: SideCropRecommendationErrorItem[] }) => {
  const errorItems = errors.map((error, i) => {
    const errorComponent = (
      <div key={`error-${error.mainCropId}-${error.sideCropId}-i`}>
        <div>{`Main Crop Id: ${error.mainCropId}`}</div>
        <div>{`Side Crop Id: ${error.sideCropId}`}</div>
        <div>{`Recommendation value: ${error.value}`}</div>
        <div>{`Errors: ${error.errorMessage}`}</div>
        {i < errors.length - 1 && <Divider className="margin-top-100 margin-bottom-100" />}
      </div>
    );
    return errorComponent;
  });

  return (
    <div className="margin-bottom-200">
      <Collapse items={[{ label: <h3 className="red-text">Errors</h3>, children: errorItems }]}></Collapse>
    </div>
  );
};

export const ModuleCropRecommendationTab = () => {
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<SideCropRecommendationErrorItem[]>([]);

  const [saving, setSaving] = useState(false);
  const signal = useCancelAsyncOnUnmount()();
  const [allSideCropRecommendations, setAllSideCropRecommendations] = useState<AllSideCropRecommendationResponse>([]);
  const { CSVReader } = useCSVReader();
  const { jsonToCSV } = usePapaParse();

  const generateAndDownloadCSV = () => {
    const csv = jsonToCSV(allSideCropRecommendations);
    saveFile(csv, "side-crop-recommendations.csv");
  };

  const loadSideCropRecommendations = () => {
    setLoading(true);
    getAllSideCropRecommendation(signal)
      .then((res) => setAllSideCropRecommendations(res))
      .finally(() => {
        setLoading(false);
      });
  };

  const processFile = (csv: CSV) => {
    setSaving(true);

    const { data } = csv;
    const [_, mainCropIdRow, ...sideCropRows] = data;

    // Structure the data to be sent to BE
    const sideCropRecommendationRequest: SideCropRecommendationRequest[] = sideCropRows
      .map((row) =>
        row.map((value, i) => {
          // skip the first 2 items (empty cell and side crop name)
          if (i < 2) return { mainCropId: "", sideCropId: "", value: "" };
          return { mainCropId: mainCropIdRow[i], sideCropId: row[1], value: value };
        })
      )
      .flat()
      .filter((input) => Object.values(input).filter((value) => value === "").length <= 1);

    updateSideCropRecommendation({ data: sideCropRecommendationRequest, signal: signal })
      .then((res) => {
        setErrors(res.errors as SideCropRecommendationErrorItem[]);
        message({
          content: `The gremlins have successfully processed ${res.processedCount} rows with ${res.errorCount} error(s)`,
          icon: "gremlin",
        });
      })
      .then(loadSideCropRecommendations)
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    loadSideCropRecommendations();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section data-testid="side-crop-recommendation-page" className="container padded-top">
      <AdminHead
        headline="Side crop recommendations"
        description='View or update Side crop recommendations here. To modify recommendations, download the CSV, add the Main Crop Id and Side Crop Id along with the corresponding values. Those values can be either "NONE", "BEFORE", "AFTER", "BOTH" or "NOT_RECOMMENDED". Re-upload the CSV. The old recommendations will be deleted and fully replaced by the content of the csv file.'
        extra={
          <>
            <Tooltip placement="bottom" title="Download this property list">
              <Button
                dataTestId="side-crop-recommendation-download-button"
                size="small"
                type="secondary"
                disabled={saving}
                loading={saving}
                onClick={generateAndDownloadCSV}
              >
                <Icon iconName="tabler/download" size="s" />
              </Button>
            </Tooltip>
            <Tooltip placement="bottom" title="Upload property list">
              <CSVReader
                config={{
                  skipEmptyLines: true,
                }}
                noDrag
                style={{
                  dropArea: {
                    border: 0,
                    padding: 0,
                    margin: 0,
                    width: "auto",
                    height: "auto",
                    display: "inline-block",
                  },
                  progressBar: {
                    display: "none !important",
                    opacity: 0,
                  },
                }}
                onUploadAccepted={(v: CSV) => processFile(v)}
              >
                {({ getRootProps }: { getRootProps: GetRootProps }) => (
                  <Button
                    className="margin-left-50"
                    disabled={saving}
                    dataTestId="side-crop-recommendation-upload-button"
                    loading={saving}
                    size="small"
                    type="secondary"
                    {...getRootProps()}
                  >
                    <Icon iconName="tabler/upload" size="s" />
                  </Button>
                )}
              </CSVReader>
            </Tooltip>
          </>
        }
      />
      <Row>
        <Col>
          {errors?.length > 0 && <CollapsibleErrors errors={errors} />}
          {/* @ts-expect-error need to change */}
          <AdminTable dataSource={allSideCropRecommendations} loading={loading || saving} showSelectedFilters={true} />
        </Col>
      </Row>
    </section>
  );
};
